import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { Avatar, ChatBubble, LoadingBar, SupportPromptGroup } from '@amzn/awsui-chat-components';
import {
  Box,
  Button,
  Container,
  FormField,
  Header,
  Modal,
  PromptInput,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useJamMyTeam } from '@/src/store/jam-myteam.context';
import { Markdown } from '@/src/components/common/Markdown';
import { useAITeammate, ChatMessage } from '@/src/hooks/store/useAITeammate';
import { useApi } from '@/src/store/api.context';
import { useUser } from '@/src/store/user.context';
import { PostAiTeammateChatResponse, TeammateChatHistoryResponse, TeammateChatMessage } from '@/src/api/GenAiAPI';
import { useActiveRouteDetails } from '@/src/hooks/useActiveRouteDetails';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { fetchAsyncWithRetry } from '@/src/utils/chat.utils';
import { useJamChat } from '@/src/store/jam-chat.context';
import { getChallengeIdFromPath } from '@/src/utils/url.utils';
import { WelcomeBubble } from '@/src/components/ai-teammate-chat/WelcomeBubble';
import { ConversationItemBubble } from '@/src/components/ai-teammate-chat/ConversationItemBubble';
import { HeaderButtonGroup } from '@/src/components/ai-teammate-chat/HeaderButtonGroup';
import ConfirmResetBubble from '@/src/components/ai-teammate-chat/ConfirmResetBubble';
import FacilitatorEscalationConfirmBubble from '@/src/components/ai-teammate-chat/FacilitatorEscalationConfirmBubble';
import { splitTextToSpans } from '@/src/utils/dom.utils';
import styles from './AITeammateChat.module.scss';
import waveStyles from '@/src/styles/wave-loading.module.scss';
import RatingContainer from '@/src/components/ui/organisms/MyJams/JamFeedback/ExpandableFeedback/RatingContainer';
import { useFlashbars } from '@/src/store/flashbar.context';
import get from 'lodash/get';
import { MILLIS_PER_MINUTE } from '@/src/constants/DateTimeConstants';
import localStorageTTLUtils from '@/src/utils/localStorageTTL.utils';

const IDLE_CHAT_TIMEOUT_TRIGGER = 5 * MILLIS_PER_MINUTE;
const FEEDBACK_THREHOLD_BEFORE_EVENT_END = 30 * MILLIS_PER_MINUTE;

/**
 * One-shot idle timer. `onIdleTrigger` returns true when it handled the idle event (prompt
 * shown or no longer applicable); returning false re-arms the timer so a later idle window can
 * still fire once its preconditions (e.g. the final-30-min window) are met without user activity.
 */
const useIdleChatTimer = (onIdleTrigger: () => boolean) => {
  const timerId = useRef<ReturnType<typeof setTimeout>>();
  // Keep a ref to the latest callback so a re-armed timer always runs current logic/state.
  const callbackRef = useRef(onIdleTrigger);
  callbackRef.current = onIdleTrigger;

  const clearTimer = () => {
    if (timerId.current) {
      clearTimeout(timerId.current);
    }
  };

  const resetTimer = () => {
    clearTimer();
    const tick = () => {
      const handled = callbackRef.current();
      if (!handled) {
        timerId.current = setTimeout(tick, IDLE_CHAT_TIMEOUT_TRIGGER);
      }
    };
    timerId.current = setTimeout(tick, IDLE_CHAT_TIMEOUT_TRIGGER);
  };

  return { resetTimer, clearTimer };
};

const MonkeyAvatar: React.FC<{ ariaLabel: string }> = ({ ariaLabel }) => (
  <Avatar loading color="gen-ai" iconName="gen-ai" imgUrl="/assets/monkey-in-circle.png" ariaLabel={ariaLabel} />
);

const JammyChatBubble: React.FC<{ ariaLabel: string; avatarAriaLabel: string }> = ({
  children,
  ariaLabel,
  avatarAriaLabel,
}) => (
  <ChatBubble avatar={<MonkeyAvatar ariaLabel={avatarAriaLabel} />} type={'incoming'} ariaLabel={ariaLabel}>
    {children}
  </ChatBubble>
);

const getFeedbackPromptShownFromStorage = (vibeId: string, eventId: string) =>
  localStorageTTLUtils.getItem(`jammy-feedback-prompted:${vibeId}:${eventId}`);
const setFeedbackPromptShownInStorage = (vibeId: string, eventId: string) =>
  localStorageTTLUtils.setItem(`jammy-feedback-prompted:${vibeId}:${eventId}`, true, 2);

const AITeammateChat: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const { t } = useTranslation();
  const { isAiTeammateGameView } = useActiveRouteDetails();
  const { event } = useJamEventDetails();
  const {
    chatHistory,
    appendMessage,
    loading,
    restoredMessages,
    setRestoredMessages,
    setLoading,
    sessionId,
    setSessionId,
    setChatHistory,
    aiTeammateActive,
    viewModalityTransfer,
    setViewModalityTransfer,
    shouldShowFeedbackBubble,
    setFeedbackBubbleShow,
  } = useAITeammate(event?.eventName || '')();
  const { addErrorFlashbar } = useFlashbars();
  const [prompt, setPrompt] = useState('');
  const [pendingConfirmation, setPendingConfirmation] = useState<{ type: string; challengeId: string } | null>(null);
  const [escalation, setEscalation] = useState<{ reason: string; summary?: string; challengeId?: string } | null>(null);
  const [confirmResetConversation, setConfirmResetConversation] = useState(false);
  const [initialHistoryLoading, setInitialHistoryLoading] = useState(!sessionId || !chatHistory?.length);
  const [featureDisabled, setFeatureDisabled] = useState(false);
  const [followUpQuestions, setFollowUpQuestions] = useState<{ id: string; text: string }[]>([]);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [starRating, setStarRating] = useState(0);
  const [feedbackBubble, setFeedbackBubble] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const { genAiAPI, jamChallengeFeedbackApi } = useApi();
  const { startSupportChat, setTriggerSupportChatSignal } = useJamChat();
  const { user } = useUser();
  const location = useLocation();

  const latestContextChallenge = useRef('');
  // Returns true when the idle event is fully handled (prompt shown, or no longer applicable) so
  // the timer stops; returns false to keep waiting (e.g. still idle but the final-30-min window
  // has not opened yet), which re-arms the idle timer.
  const onIdleTrigger = (): boolean => {
    // Not eligible at all (already prompted / too few messages / no event) — stop.
    if (!shouldShowFeedbackBubble() || !event?.eventEndDate) {
      return true;
    }
    const remainingToEventEnd = new Date(event.eventEndDate).getTime() - Date.now();
    // Event already ended — stop; prompting after the event is over is pointless.
    if (remainingToEventEnd <= 0) {
      return true;
    }
    // Eligible but the final-30-min window has not opened yet — keep waiting.
    if (remainingToEventEnd >= FEEDBACK_THREHOLD_BEFORE_EVENT_END) {
      return false;
    }
    setFeedbackBubble(true);
    // Mark shown in both the session store (gates shouldShowFeedbackBubble) and durable storage,
    // so idle timers re-armed later this session do not re-trigger the prompt.
    setFeedbackBubbleShow(true);
    setFeedbackPromptShownInStorage(user?.vibeId || '', event?.eventName || '');
    return true;
  };
  const { resetTimer, clearTimer } = useIdleChatTimer(onIdleTrigger);

  const restoredMessageCount = restoredMessages?.length || 0;
  const { myTeam } = useJamMyTeam();
  const initializedEventRef = useRef<string | null>(null);
  const isFullScreen = isAiTeammateGameView;

  const sendMessage = useCallback(
    async (message: string): Promise<PostAiTeammateChatResponse | null> => {
      resetTimer();
      if (!event || !event?.eventName) {
        throw Error('event ID is required');
      }

      if (!user) {
        throw Error('User profile not loaded');
      }

      setLoading(true);

      const teamname = myTeam?.team?.teamId ?? myTeam?.team?.teamName ?? '';

      try {
        const response = await genAiAPI.postAiTeammateChat(
          event.eventName,
          user.vibeId,
          message,
          sessionId,
          latestContextChallenge.current,
          teamname
        );
        if (!sessionId && 'sessionId' in response) {
          setSessionId(response.sessionId);
        }

        const requiresAsyncResponse = response.tool !== 'respond' && response.status === 'processing' && response.s3Url;

        if (!requiresAsyncResponse) {
          appendMessage({
            messageId: response.jobId || crypto.randomUUID(),
            role: 'assistant',
            content: response.message || '',
            timestamp: new Date().toISOString(),
            asyncInProgress: false,
          });
        } else {
          try {
            const asyncResult = await fetchAsyncWithRetry(response.s3Url);
            appendMessage({
              messageId: response.jobId || crypto.randomUUID(),
              role: 'assistant',
              content: asyncResult.text,
              timestamp: new Date().toISOString(),
              asyncInProgress: false,
            });
            if (!!asyncResult?.pendingConfirmation?.type) {
              setPendingConfirmation(asyncResult.pendingConfirmation);
            }
            if (!!asyncResult?.followUpQuestions) {
              setFollowUpQuestions(asyncResult.followUpQuestions.map((text) => ({ id: crypto.randomUUID(), text })));
            }
            if (!!asyncResult?.escalation?.reason) {
              setEscalation(asyncResult.escalation);
            }
          } catch (err) {
            // Finalize the in-progress bubble on failure; otherwise it stays in the
            // "processing" state forever with an empty body and a permanent loading bar.
            // Do NOT rethrow: the error is already surfaced in this bubble, so letting it
            // propagate to handleSend would append a second, duplicate error bubble.
            // Log the raw error for debugging, but show the user a generic message to keep
            // UX consistent with handleSend and avoid surfacing arbitrary upstream detail.
            console.error(err);
            appendMessage({
              content: t(i18nKeys.conversationalAI.genericError),
              asyncInProgress: false,
              messageId: response.jobId || crypto.randomUUID(),
              role: 'assistant',
              timestamp: new Date().toISOString(),
            });
          }
        }
        return response;
      } catch (err) {
        console.error(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [genAiAPI, user, sessionId, appendMessage, myTeam?.team, event, t]
  );

  const initialize = useCallback(
    async (eventId?: string | null) => {
      const MAX_RESULTS = 50;
      const MAX_CALLS = 5;
      if (!eventId) {
        setInitialHistoryLoading(false);
        return;
      }
      const hasBeenPromptedForFeedbackBefore = getFeedbackPromptShownFromStorage(user?.vibeId || '', eventId);
      setFeedbackBubbleShow(hasBeenPromptedForFeedbackBefore);
      try {
        const restored: ChatMessage[] = [];
        let response: TeammateChatHistoryResponse | null = null;
        let nextToken: string | null | undefined = '';
        let numberOfCalls = 0;
        do {
          response = await genAiAPI.getTeammateChatHistory(eventId, sessionId, MAX_RESULTS, nextToken);
          if (response.messages && response.messages.length) {
            restored.push(
              ...response.messages.map((m: TeammateChatMessage) => ({
                ...m,
                asyncInProgress: m.asyncInProgress ?? false,
              }))
            );
          }
          nextToken = response?.nextToken;
        } while (nextToken && ++numberOfCalls < MAX_CALLS);
        if (!sessionId && response.sessionId) {
          setSessionId(response.sessionId);
        }
        if (restored?.length) {
          setRestoredMessages(restored);
          setChatHistory(restored);
        }
        if (!!response?.pendingConfirmation?.type) {
          setPendingConfirmation(response.pendingConfirmation);
        }
      } catch (err) {
        console.error('Failed to load chat history', err);
        if (get(err, 'error.code') === 'FeatureDisabled') {
          // feature flag is off
          setFeatureDisabled(true);
        }
      } finally {
        setInitialHistoryLoading(false);
        resetTimer();
      }
    },
    [genAiAPI, sessionId]
  );

  useEffect(() => {
    // Load history only once per event. `initialize` calls setSessionId, which would
    // otherwise change its identity and re-trigger this effect, re-fetching history
    // and clobbering any messages the user sent between the two fetches.
    if (event?.eventName && initializedEventRef.current !== event.eventName) {
      initializedEventRef.current = event.eventName;
      initialize(event.eventName);
    } else if (!event?.eventName) {
      setInitialHistoryLoading(false);
    }
  }, [event?.eventName, initialize]);

  const handleSend = async () => {
    if (!prompt.trim()) return;

    // Clear any outstanding confirmation/escalation prompt: sending a new message instead
    // of acting on the prompt supersedes it. Otherwise the stale bubble (gated on !loading)
    // would reappear once this send finishes. A fresh prompt from this send's async result
    // is set later, inside the awaited sendMessage, so it is not clobbered here.
    setPendingConfirmation(null);
    setEscalation(null);
    setFollowUpQuestions([]);

    const userMessage: ChatMessage = {
      messageId: `user-${Date.now()}`,
      role: 'user',
      content: prompt,
      timestamp: new Date().toISOString(),
      asyncInProgress: false,
    };
    appendMessage(userMessage);
    const currentPrompt = prompt;
    setPrompt('');
    // setLoading(true);

    try {
      // The response is appended to chatHistory by sendMessage; no local copy is needed.
      await sendMessage(currentPrompt);
    } catch (err) {
      console.error(err);
      // sendMessage throws before the assistant bubble is appended (e.g. missing event ID /
      // user, or postAiTeammateChat rejecting), so the last message here is the user's. Append
      // a new assistant error bubble instead of overwriting the user's message.
      appendMessage({
        messageId: `error-${Date.now()}`,
        role: 'assistant',
        content: t(i18nKeys.conversationalAI.genericError),
        timestamp: new Date().toISOString(),
        asyncInProgress: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePendingConfirmation = async (response: string): Promise<boolean> => {
    setLoading(true);
    // Clear the current confirmation up front rather than after sendMessage resolves.
    // sendMessage may set a *new* pendingConfirmation from its async result (a chained,
    // multi-step confirmation); resetting after the await would clobber that. The prompt
    // is hidden while loading, so clearing here has no visible flicker.
    setPendingConfirmation(null);
    try {
      await sendMessage(response);
      // Signal that the confirmation was actually submitted to the backend. Callers (handleConfirm)
      // gate destructive follow-up actions on this - a swallowed failure must NOT look like success.
      return true;
    } catch (err) {
      console.error('[AITeammateChat] Reject error:', err);
      appendMessage({
        messageId: `error-${Date.now()}`,
        role: 'assistant',
        content: t(i18nKeys.conversationalAI.genericError),
        timestamp: new Date().toISOString(),
        asyncInProgress: false,
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => handlePendingConfirmation(t(i18nKeys.general.yes));
  const handleReject = () => handlePendingConfirmation(t(i18nKeys.general.no));

  const handleStartEscalation = async () => {
    if (!!event?.eventName) {
      // Set loading up front (mirrors handlePendingConfirmation). The escalation prompt is
      // gated on !loading, so this hides it while the request is in flight and prevents a
      // double-click from firing duplicate startSupportChat calls.
      setLoading(true);
      try {
        const response = await startSupportChat(
          event?.eventName,
          escalation?.challengeId || null,
          escalation?.summary || escalation?.reason || t(i18nKeys.conversationalAI.escalation.defaultReason)
        );
        if (response?.id) {
          setTriggerSupportChatSignal(response.id);
        }
        setEscalation(null);
      } catch (err) {
        console.error(err);
        appendMessage({
          messageId: `error-${Date.now()}`,
          role: 'assistant',
          content: t(i18nKeys.conversationalAI.genericError),
          timestamp: new Date().toISOString(),
          asyncInProgress: false,
        });
      } finally {
        setLoading(false);
      }
    } else {
      console.error('missing event name', event?.eventName);
    }
  };
  const handleCancelEscalation = () => setEscalation(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollBottomRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const THRESHOLD = 100;

  const handleScroll = () => {
    const el = scrollRef.current;
    if (el) {
      isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= THRESHOLD;
    }
  };

  const lastMessageContent = chatHistory[chatHistory.length - 1]?.content ?? '';

  useEffect(() => {
    setTimeout(function () {
      if (scrollBottomRef.current) {
        scrollBottomRef.current.scrollIntoView({
          block: 'end',
          behavior: viewModalityTransfer ? undefined : 'smooth',
        });
        if (viewModalityTransfer) {
          setViewModalityTransfer(false);
        }
      }
    }, 50);
  }, [
    chatHistory.length,
    lastMessageContent,
    confirmResetConversation,
    escalation,
    pendingConfirmation,
    feedbackBubble,
  ]);

  useEffect(() => {
    const currentChallengeId = getChallengeIdFromPath(location.pathname);
    if (!!currentChallengeId) {
      latestContextChallenge.current = currentChallengeId;
    }
  }, [location.pathname]);

  const footerResizeObserver = useRef<ResizeObserver | undefined>();
  const [footerHeight, setFooterHeight] = useState(60);

  useEffect(() => {
    handleScroll();
    const eventFooter = document.getElementById('event-footer');
    if (!eventFooter) return;
    footerResizeObserver.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { blockSize } = entry.borderBoxSize[0];
        setFooterHeight(blockSize);
      }
    });
    footerResizeObserver.current!.observe(eventFooter);
    return () => {
      if (footerResizeObserver?.current) {
        footerResizeObserver?.current?.disconnect();
      }
      clearTimer();
    };
  }, []);

  const overallLoading = loading || initialHistoryLoading;

  return (
    <div
      style={{ height: isFullScreen ? '75vh' : `calc(100vh - ${Math.round(footerHeight + 80)}px)`, padding: '1rem 0' }}
    >
      <Container
        disableContentPaddings
        fitHeight
        header={
          <HeaderButtonGroup
            eventName={event?.eventName || ''}
            disabled={featureDisabled || overallLoading}
            onResetConversation={() => setConfirmResetConversation(true)}
            isFullScreen={isFullScreen}
            onFeedbackModalActivation={() => setShowFeedbackModal(true)}
          />
        }
        footer={
          <>
            {overallLoading && <LoadingBar variant="gen-ai" />}
            <PromptInput
              value={prompt}
              onChange={({ detail }) => {
                setPrompt(detail.value);
                resetTimer();
              }}
              minRows={3}
              actionButtonIconName="send"
              placeholder={t(i18nKeys.conversationalAI.promptPlaceholder)}
              disabled={overallLoading || featureDisabled || confirmResetConversation || !aiTeammateActive}
              onAction={handleSend}
            />
          </>
        }
      >
        <div ref={scrollRef} onScroll={handleScroll} className={styles.chatHistoryContainer}>
          <SpaceBetween direction="vertical" size={compact ? 's' : 'm'}>
            <SpaceBetween direction="vertical" size={compact ? 'xxs' : 's'}>
              {!featureDisabled && aiTeammateActive && <WelcomeBubble onQuestionClick={(q) => setPrompt(q)} />}
              {chatHistory.map((c, index) => (
                <React.Fragment key={c.messageId}>
                  {index === restoredMessageCount && restoredMessageCount > 0 && (
                    <Box textAlign="center" color="text-status-inactive" padding={{ vertical: 'xs' }}>
                      <hr className={styles.restoredHistoryDivider} />
                      <Box variant="small" color="text-status-inactive">
                        {t(i18nKeys.conversationalAI.newMessages)}
                      </Box>
                    </Box>
                  )}
                  <ConversationItemBubble
                    showLoadingBar={c.asyncInProgress}
                    messageId={c.messageId}
                    ariaLabel={t(i18nKeys.conversationalAI.ariaLabels.message, {
                      role: c.role,
                      timestamp: new Date(c.timestamp).toLocaleString(),
                    })}
                    role={c.role}
                    triggerFeedbackModal={() => setShowFeedbackModal(true)}
                    onThumbsUpDown={(action) => {
                      if (event?.eventName) {
                        jamChallengeFeedbackApi
                          .saveEventJammyFeedback(
                            event?.eventName,
                            {
                              askJammyThumbsUp: action === 'thumbs-up',
                              askJammyThumbsDown: action === 'thumbs-down',
                            },
                            true
                          )
                          .then(() => {
                            // Mark shown in both the session store and durable storage so the idle
                            // prompt does not also fire later this session after a thumbs rating.
                            setFeedbackBubbleShow(true);
                            setFeedbackPromptShownInStorage(user?.vibeId || '', event.eventName);
                          })
                          .catch(() => {
                            // Silent submit; the API client suppresses the flashbar and a failed thumbs rating is non-critical.
                          });
                      }
                    }}
                  >
                    <Markdown content={c.content} />
                  </ConversationItemBubble>
                </React.Fragment>
              ))}
            </SpaceBetween>

            {pendingConfirmation && pendingConfirmation?.type && !overallLoading && (
              <JammyChatBubble
                avatarAriaLabel={t(i18nKeys.conversationalAI.ariaLabels.assistant)}
                ariaLabel={t(i18nKeys.conversationalAI.ariaLabels.confirmationRequired)}
              >
                <SpaceBetween direction="vertical" size="s">
                  <span>
                    {t(i18nKeys.conversationalAI.confirmation.prompt)}{' '}
                    <strong className={styles.confirmationTypeLabel}>
                      {pendingConfirmation?.type === 'restart_lab_environment'
                        ? t(i18nKeys.conversationalAI.confirmation.restartLabEnvironment)
                        : pendingConfirmation.type.replaceAll('_', ' ')}
                    </strong>
                  </span>
                  <SpaceBetween direction="horizontal" size="s">
                    <Button variant="primary" onClick={handleConfirm}>
                      {t(i18nKeys.conversationalAI.confirmation.confirm)}
                    </Button>
                    <Button onClick={handleReject}>{t(i18nKeys.conversationalAI.confirmation.cancel)}</Button>
                  </SpaceBetween>
                </SpaceBetween>
              </JammyChatBubble>
            )}

            {escalation && escalation?.reason && !overallLoading && (
              <FacilitatorEscalationConfirmBubble onCancel={handleCancelEscalation} onConfirm={handleStartEscalation} />
            )}

            {/* Only show the unavailable notice once the teammate state is confirmed disabled
                (aiTeammateActive === false), not while it is still undefined/initializing, to
                avoid a flash of this bubble before JamEventView's effect resolves the value. */}
            {(featureDisabled || aiTeammateActive === false) && (
              <JammyChatBubble
                avatarAriaLabel={t(i18nKeys.conversationalAI.ariaLabels.assistant)}
                ariaLabel={t(i18nKeys.conversationalAI.ariaLabels.featureUnavailable)}
              >
                <SpaceBetween direction="vertical" size="s">
                  <span>{t(i18nKeys.conversationalAI.featureUnavailable)}</span>
                </SpaceBetween>
              </JammyChatBubble>
            )}
            {confirmResetConversation && (
              <ConfirmResetBubble
                onCancel={() => setConfirmResetConversation(false)}
                onConfirm={() => {
                  setChatHistory([]);
                  setRestoredMessages([]);
                  setPendingConfirmation(null);
                  setSessionId('');
                  setConfirmResetConversation(false);
                  setFollowUpQuestions([]);
                }}
              />
            )}
            {overallLoading && (
              <JammyChatBubble
                avatarAriaLabel={t(i18nKeys.conversationalAI.ariaLabels.assistant)}
                ariaLabel="Generating response"
              >
                <div data-testid="ai-teammate__loading-bubble" className={waveStyles['wave-text']}>
                  {splitTextToSpans('...')}
                </div>
              </JammyChatBubble>
            )}
            {feedbackBubble && (
              <JammyChatBubble
                avatarAriaLabel={t(i18nKeys.conversationalAI.ariaLabels.assistant)}
                ariaLabel={t(i18nKeys.conversationalAI.jammyFeedback.idlePromptAriaLabel)}
              >
                <Button
                  variant="inline-link"
                  onClick={() => {
                    setShowFeedbackModal(true);
                    setFeedbackBubble(false);
                  }}
                >
                  {t(i18nKeys.conversationalAI.jammyFeedback.idlePrompt)}
                </Button>
              </JammyChatBubble>
            )}
            {!!followUpQuestions?.length && (
              <SupportPromptGroup
                onItemClick={({ detail }) => {
                  const selectedFollowUp = followUpQuestions.find((question) => question.id === detail.id);
                  if (selectedFollowUp) {
                    setPrompt(selectedFollowUp.text);
                  }
                }}
                items={followUpQuestions}
                ariaLabel={'Follow up questions'}
              />
            )}
          </SpaceBetween>
          <div
            className={styles.hiddenScrollAnchor}
            data-testid="ai-teammate-chat__hidden-scroll-anchor"
            ref={scrollBottomRef}
          />
        </div>
      </Container>
      <Modal
        visible={showFeedbackModal}
        data-testid="ai-teammate__feedback-modal"
        onDismiss={() => setShowFeedbackModal(false)}
        header={<Header>{t(i18nKeys.conversationalAI.jammyFeedback.modalHeader)}</Header>}
        footer={
          <Box textAlign="right">
            <Button
              variant="primary"
              data-testid="ai-teammate__submit-feedback"
              loading={submittingFeedback}
              disabled={submittingFeedback}
              onClick={async () => {
                if (!event?.eventName) {
                  addErrorFlashbar(t(i18nKeys.conversationalAI.jammyFeedback.missingEventId));
                  return;
                }
                // Modal-scoped loading so the in-flight submit does not trigger the chat's gen-ai loading UI.
                setSubmittingFeedback(true);
                try {
                  await jamChallengeFeedbackApi.saveEventJammyFeedback(event.eventName, {
                    askJammyRating: starRating,
                    askJammyFeedback: feedbackText,
                  });
                  // Only clear input and close on success so a failed submit keeps the user's text for retry.
                  setShowFeedbackModal(false);
                  setFeedbackText('');
                  setStarRating(0);
                } catch {
                  // The API client already surfaces an error flashbar via its failMessage; the modal stays open for retry.
                  setShowFeedbackModal(true);
                } finally {
                  setSubmittingFeedback(false);
                }
              }}
            >
              {t(i18nKeys.general.submit)}
            </Button>
          </Box>
        }
      >
        <RatingContainer
          label={t(i18nKeys.conversationalAI.jammyFeedback.ratingLabel)}
          data-testid="ai-teammate__feedback-modal-rating"
          rating={starRating}
          setRating={setStarRating}
        />
        <FormField constraintText={t(i18nKeys.conversationalAI.jammyFeedback.otherFeedback)}>
          <Textarea
            value={feedbackText}
            data-testid="ai-teammate__feedback-modal-textarea"
            onChange={({ detail }) => setFeedbackText(detail.value)}
          />
        </FormField>
      </Modal>
    </div>
  );
};

export default AITeammateChat;
